import axios from "axios";
import {
  currencyFormatter,
  parsePriceWithCurrency,
  parseCurrencyCode,
  fetchAllPrices,
} from "./dynamic-pricing";

const SCREENPRESSO_PRO = 554502;
const SCREENPRESSO_ENTERPRISE = 555753;

// https://paddle.com/docs/offering-volume-discounts/
// https://paddle.com/docs/overriding-checkout-prices/
// https://paddle.com/docs/checkout-parameters/
const volumeDiscountPrices = [
  {
    quantity: 1,
    discount: 0,
    price: "29.99",
    auth: "5b0b99c448942e0a4a8f603cfdbcf530",
  },
  {
    quantity: 2,
    discount: 0.1,
    price: "26.99",
    auth: "008f0d86a7de49e9ddf6c484c143fcc1",
    message: "Volume discount: 10%",
  },
  {
    quantity: 5,
    discount: 0.2,
    price: "23.99",
    auth: "66a66d5b26b715ab46b4ea4713cd4408",
    message: "Volume discount: 20%",
  },
  {
    quantity: 10,
    discount: 0.3,
    price: "20.99",
    auth: "958d0852e283712bb63a7144398a44d7",
    message: "Volume discount: 30%",
  },
];

const pricing = quantity =>
  volumeDiscountPrices.filter(i => quantity <= i.quantity)[0] ||
  volumeDiscountPrices[volumeDiscountPrices.length - 1];

// Not checkout => contact us will be used instead.
export const enterpriseCheckout = () => undefined;

const fetchBasePrice = () => {
  return new Promise(resolve =>
    Paddle.Product.Prices(SCREENPRESSO_PRO, data => {
      /*{
        "price": {
          "gross": "€29.99",
          "net": "€24.99",
          "tax": "€5.00",
          "tax_included": true
        },
        "quantity": 1,
        "country": "FR"
      }*/

      data.price.grossValue = parsePriceWithCurrency(data.price.gross);
      data.price.netValue = parsePriceWithCurrency(data.price.net);
      data.price.taxValue = parsePriceWithCurrency(data.price.tax);
      data.price.currency = parseCurrencyCode(data.price.gross);
      data.price.quantity = data.quantity;
      data.price.country = data.country;
      resolve(data.price);
    })
  );
};

export const ProCheckout = async (quantity = 1, options = {}) => {
  const basePrice = await fetchBasePrice();
  const override = pricing(quantity);
  const paddleOptions = {
    product: SCREENPRESSO_PRO,
    quantity,
    allowQuantity: quantity < 2,
    message: override.message,
    email: options.email,
    passthrough: options,
    coupon: options && options.reseller ? 'RESELLER_2019': undefined,
  };

  if (basePrice.currency === "USD") {
    // Override USD
    paddleOptions.prices = [
      {
        currency: "USD",
        price: override.price,
        auth: override.auth,
      },
    ];
  } else {
    // Override only EUR (default currency)
    // to provide automatic conversion rate for other currency
    paddleOptions.price = override.price;
    paddleOptions.auth = override.auth;
  }

  Paddle.Checkout.open(paddleOptions);
};

const fetchBasePrice1 = reseller => {
  // CORS are restricted
  const uri = `https://checkout.paddle.com/api/2.0/prices?product_ids=554502&coupons=${
    reseller ? "RESELLER_2019" : ""
  }`;
  /*
  {
    success: true,
    response: {
      customer_country: "FR",
      products: [
        {
          product_id: 554502,
          product_title: "Screenpresso PRO",
          currency: "EUR",
          vendor_set_prices_included_tax: true,
          price: {
          gross: 29.99,
          net: 24.99,
          tax: 5
        },
        list_price: {
          gross: 29.99,
          net: 24.99,
          tax: 5
        },
        applied_coupon: [ ]
      }
      ]
    }
  }
  */

  return axios.get(uri).then(data => {
    const product = data.response.products[0];
    const price = {
      grossValue: product.gross,
      netValue: product.net,
      taxValue: product.tax,
      currency: product.currency,
      country: data.response.customer_country,
    };

    return price;
  });
};

export const fetchProPrice = async (
  quantity,
  { reseller } = { reseller: false }
) => {
  const basePrice = await fetchBasePrice();
  const quantityPricing = pricing(quantity);
  const resellerDiscount = reseller ? 0.1 : 0;

  const formatCurrency = currencyFormatter(
    basePrice.country,
    basePrice.currency
  );
  const price = {
    quantity,
    paddle: {
      productID: SCREENPRESSO_PRO,
    },
    totalWithTax: formatCurrency(
      basePrice.grossValue *
        quantity *
        (1 - quantityPricing.discount) *
        (1 - resellerDiscount)
    ),
    totalWithoutTax: formatCurrency(
      (basePrice.grossValue - basePrice.taxValue) *
        quantity *
        (1 - quantityPricing.discount) *
        (1 - resellerDiscount)
    ),
    totalWithTaxWithoutDiscount: formatCurrency(
      basePrice.grossValue * quantity
    ),
    tax: formatCurrency(
      basePrice.taxValue *
        quantity *
        (1 - quantityPricing.discount) *
        (1 - resellerDiscount)
    ),
    isDiscount: quantityPricing.discount + resellerDiscount > 0,
    discountTotal: formatCurrency(
      basePrice.grossValue *
        quantity *
        (quantityPricing.discount + (1 - quantityPricing.discount) * resellerDiscount)
    ),
    country: basePrice.country,
  };
  return price;
};

export const fetchEnterprisePrice = async (
  quantity,
  { reseller } = { reseller: false }
) => {
  const remotePricing = await fetchAllPrices();
  const netPrice = remotePricing.price_site;
  const netValue = parsePriceWithCurrency(netPrice);
  const currency = parseCurrencyCode(netPrice);
  const resellerDiscount = reseller ? 0.1 : 0;

  // Ask Paddle the country
  const basePrice = await fetchBasePrice();
  const country = basePrice.country;
  const formatCurrency = currencyFormatter(country, currency);

  const price = {
    quantity,
    paddle: {
      productID: SCREENPRESSO_ENTERPRISE,
    },
    totalWithTax: formatCurrency(netValue * (1 - resellerDiscount)),
    totalWithoutTax: formatCurrency(netValue * (1 - resellerDiscount)),
    totalWithTaxWithoutDiscount: formatCurrency(netValue),
    tax: formatCurrency(0),
    isDiscount: resellerDiscount > 0,
    discountTotal: formatCurrency(netValue * resellerDiscount),
    country,
  };

  return price;
};
